import { useEffect, useState } from "react";
import api from "@/admin/api";
import { useAuth } from "@/admin/AuthContext";
import { Eyebrow } from "@/components/comiq/Eyebrow";
import { COLORS } from "@/comiq/tokens";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { DeleteDialog } from "@/pages/admin/DeleteDialog";

export default function Organizations() {
  const { user } = useAuth();
  const canEdit = ["platform_admin", "research_admin"].includes(user?.role);
  const canDelete = user?.role === "platform_admin";
  const [orgs, setOrgs] = useState([]);
  const [form, setForm] = useState({ name: "", industry: "", size: "", is_demo: true });
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // org being edited
  const [editForm, setEditForm] = useState({ name: "", industry: "", size: "" });
  const [deleting, setDeleting] = useState(null); // org being deleted/archived

  const load = () => api.get("/admin/organizations").then((r) => setOrgs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/organizations", form);
      toast.success("Organisasi ditambahkan");
      setForm({ name: "", industry: "", size: "", is_demo: true });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (o) => {
    setEditing(o);
    setEditForm({ name: o.name || "", industry: o.industry || "", size: o.size || "" });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/admin/organizations/${editing.org_id}`, editForm);
      toast.success("Organisasi diperbarui");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memperbarui");
    } finally {
      setSaving(false);
    }
  };

  const onDeleted = (res) => {
    if (res?.archived) {
      toast.success("Organisasi diarsipkan — histori response, scoring, dan audit tetap utuh.");
    } else {
      toast.success("Organisasi dihapus permanen. Backup tersimpan.");
    }
    load();
  };

  const inputCls = "w-full rounded-comiq-sm border bg-white px-3 py-2 text-sm outline-none focus:border-comiq-orange";
  const inputStyle = { borderColor: COLORS.line, color: COLORS.ink };

  return (
    <div className="p-6">
      <Eyebrow>Master Data</Eyebrow>
      <h1 className="font-serif-editorial mt-1 text-3xl" style={{ color: COLORS.ink }}>Organisasi</h1>
      <p className="mt-1 text-sm" style={{ color: COLORS.textSoft }}>ID organisasi tetap lintas gelombang — respons selalu terikat pada organisasi &amp; gelombangnya.</p>

      {canEdit && (
        <form onSubmit={submit} className="mt-6 rounded-comiq border bg-comiq-surface-alt p-5" style={{ borderColor: COLORS.line }}>
          <h2 className="text-sm font-semibold" style={{ color: COLORS.ink }}>Tambah organisasi</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <input data-testid="org-name-input" className={inputCls} style={inputStyle} placeholder="Nama kanonis *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <input data-testid="org-industry-input" className={inputCls} style={inputStyle} placeholder="Industri" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} />
            <input data-testid="org-size-input" className={inputCls} style={inputStyle} placeholder="Ukuran (mis. 500-1000)" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} />
          </div>
          <label className="mt-3 flex items-center gap-2 text-xs" style={{ color: COLORS.textSoft }}>
            <input type="checkbox" checked={form.is_demo} onChange={(e) => setForm({ ...form, is_demo: e.target.checked })} data-testid="org-demo-checkbox" />
            Tandai sebagai data demo
          </label>
          <button type="submit" disabled={saving} className="btn-comiq btn-accent mt-4" data-testid="org-submit-btn">
            <Plus size={15} /> {saving ? "Menyimpan…" : "Tambah"}
          </button>
        </form>
      )}

      <div className="mt-6 overflow-hidden rounded-comiq border bg-comiq-surface-alt" style={{ borderColor: COLORS.line }}>
        <table className="w-full text-sm" data-testid="orgs-table">
          <thead>
            <tr style={{ background: COLORS.surface, color: COLORS.textSoft }}>
              {["Nama", "Industri", "Ukuran", "Label", "Dibuat", ...(canEdit || canDelete ? ["Aksi"] : [])].map((h) => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orgs.map((o) => (
              <tr key={o.org_id} className="border-t" style={{ borderColor: COLORS.line }} data-testid={`org-row-${o.org_id}`}>
                <td className="px-4 py-3 font-medium" style={{ color: COLORS.ink }}>{o.name}</td>
                <td className="px-4 py-3" style={{ color: COLORS.textSoft }}>{o.industry || "—"}</td>
                <td className="px-4 py-3" style={{ color: COLORS.textSoft }}>{o.size || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1.5">
                    {o.is_demo ? (
                      <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase" style={{ background: "#FEF3C7", color: "#B45309" }}>Demo</span>
                    ) : (
                      <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase" style={{ background: "#DCFCE7", color: "#15803D" }}>Produksi</span>
                    )}
                    {o.status === "archived" && (
                      <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase" style={{ background: "#EEEAE0", color: "#57534E" }} data-testid={`org-archived-${o.org_id}`}>Diarsipkan</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs" style={{ color: COLORS.textSoft }}>{new Date(o.created_at).toLocaleDateString("id-ID")}</td>
                {(canEdit || canDelete) && (
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      {canEdit && (
                        <button onClick={() => openEdit(o)} className="rounded-full border p-1.5" style={{ borderColor: COLORS.line }} title="Edit organisasi" data-testid={`org-edit-${o.org_id}`}>
                          <Pencil size={13} style={{ color: COLORS.ink }} />
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => setDeleting(o)} className="rounded-full border p-1.5" style={{ borderColor: COLORS.line }} title="Hapus / arsipkan organisasi" data-testid={`org-delete-${o.org_id}`}>
                          <Trash2 size={13} style={{ color: "#B91C1C" }} />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="org-edit-dialog">
          <form onSubmit={saveEdit} className="w-full max-w-md rounded-comiq-lg border bg-comiq-surface-alt p-6" style={{ borderColor: COLORS.line }}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold" style={{ color: COLORS.ink }}>Edit organisasi</h2>
              <button type="button" onClick={() => setEditing(null)} className="rounded-full border p-1.5" style={{ borderColor: COLORS.line }} data-testid="org-edit-cancel-btn"><X size={14} /></button>
            </div>
            <p className="mt-1 text-xs" style={{ color: COLORS.textSoft }}>Label PRODUKSI/DEMO dikelola sistem dan tidak dapat diubah.</p>
            <label className="mt-4 block text-xs font-semibold" style={{ color: COLORS.ink }}>Nama organisasi *</label>
            <input className={`${inputCls} mt-1`} style={inputStyle} value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required data-testid="org-edit-name-input" />
            <label className="mt-3 block text-xs font-semibold" style={{ color: COLORS.ink }}>Industri</label>
            <input className={`${inputCls} mt-1`} style={inputStyle} value={editForm.industry} onChange={(e) => setEditForm({ ...editForm, industry: e.target.value })} data-testid="org-edit-industry-input" />
            <label className="mt-3 block text-xs font-semibold" style={{ color: COLORS.ink }}>Ukuran</label>
            <input className={`${inputCls} mt-1`} style={inputStyle} value={editForm.size} onChange={(e) => setEditForm({ ...editForm, size: e.target.value })} data-testid="org-edit-size-input" />
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setEditing(null)} className="btn-comiq btn-line">Batal</button>
              <button type="submit" disabled={saving} className="btn-comiq btn-accent disabled:opacity-40" data-testid="org-edit-save-btn">{saving ? "Menyimpan…" : "Simpan"}</button>
            </div>
          </form>
        </div>
      )}

      {deleting && <DeleteDialog kind="organization" id={deleting.org_id} onClose={() => setDeleting(null)} onDeleted={onDeleted} />}
    </div>
  );
}
